import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  MessageSquare,
  TrendingDown,
  TrendingUp,
  Minus,
  ChevronRight,
  ChevronLeft,
  X,
  ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function WeeklyReviews({ user, onLogout }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [recentCheckins, setRecentCheckins] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [mealPlan, setMealPlan] = useState(null);
  const [adjusting, setAdjusting] = useState(false);
  const [tierChanges, setTierChanges] = useState([]); // Track tier adjustments made
  const [coachFeedback, setCoachFeedback] = useState(''); // Custom feedback message
  const [markingReviewed, setMarkingReviewed] = useState(false);
  const [showAnswersPopup, setShowAnswersPopup] = useState(false); // For adherence score popup

  useEffect(() => {
    if (user?.role !== 'coach') {
      navigate('/app');
      return;
    }
    fetchRecentCheckins();
  }, [user, navigate]);

  const fetchRecentCheckins = async () => {
    setLoading(true);
    try {
      // Get all clients (axios interceptor handles auth)
      const clientsRes = await axios.get(`${API}/coach/clients`);
      const clients = clientsRes.data || [];
      
      // Get check-ins for all clients from last 7 days
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      
      const checkinsPromises = clients.map(async (client) => {
        try {
          const checkinsRes = await axios.get(`${API}/checkins`, {
            params: { user_id: client.id }
          });
          
          const clientCheckins = checkinsRes.data || [];
          
          // Filter for recent check-ins that are NOT reviewed
          const recentClientCheckins = clientCheckins.filter(checkin => {
            const checkinDate = new Date(checkin.created_at || checkin.submitted_at);
            const isRecent = checkinDate >= sevenDaysAgo;
            const isNotReviewed = !checkin.review_status || checkin.review_status !== 'reviewed';
            return isRecent && isNotReviewed;
          });
          
          if (recentClientCheckins.length > 0) {
            // Get the most recent one
            const latest = recentClientCheckins.sort((a, b) => 
              new Date(b.created_at || b.submitted_at) - new Date(a.created_at || a.submitted_at)
            )[0];
            
            // Try to get weight logs (don't fail if this errors)
            let weightLogs = [];
            let startingWeight = null;
            let startingWeightDate = null;
            try {
              const weightRes = await axios.get(`${API}/weight-logs`, {
                params: { user_id: client.id }
              });
              weightLogs = (weightRes.data || []).sort((a, b) => 
                new Date(a.date || a.created_at) - new Date(b.date || b.created_at) // Oldest first
              );
              
              // Get starting weight (first weight log)
              if (weightLogs.length > 0) {
                startingWeight = weightLogs[0].weight;
                startingWeightDate = weightLogs[0].date || weightLogs[0].created_at;
              }
            } catch (err) {
              console.warn(`Failed to fetch weight logs for client ${client.id}:`, err.message);
            }
            
            // Try to get onboarding weight if no weight logs exist
            let onboardingCreatedAt = null;
            if (!startingWeight) {
              try {
                const onboardingRes = await axios.get(`${API}/onboarding/${client.id}`);
                startingWeight = onboardingRes.data?.answers_json?.weight || null;
                startingWeightDate = onboardingRes.data?.created_at;
                onboardingCreatedAt = onboardingRes.data?.created_at;
              } catch (err) {
                console.warn(`Failed to fetch onboarding for client ${client.id}:`, err.message);
              }
            }
            
            // Current weight is the most recent log
            const currentWeight = weightLogs[weightLogs.length - 1]?.weight || null;
            const currentWeightDate = weightLogs[weightLogs.length - 1]?.date || weightLogs[weightLogs.length - 1]?.created_at || null;
            
            // Calculate weekly average
            let weeklyAvg = null;
            if (startingWeight && currentWeight && onboardingCreatedAt) {
              const startDate = new Date(onboardingCreatedAt);
              const currentDate = new Date();
              const daysElapsed = (currentDate - startDate) / (1000 * 60 * 60 * 24);
              const weeksElapsed = Math.floor(daysElapsed / 7);
              
              if (weeksElapsed >= 2) {
                const totalChange = currentWeight - startingWeight;
                weeklyAvg = (totalChange / weeksElapsed).toFixed(2);
              }
            }
            
            const score = ((latest.consistency || 0) + (latest.hunger || 0) + (latest.energy || 0)) / 3;
            
            return {
              client,
              checkin: latest,
              score: score.toFixed(1),
              weightLogs,
              startingWeight,
              startingWeightDate,
              currentWeight,
              currentWeightDate,
              weeklyAvg
            };
          }
          return null;
        } catch (error) {
          return null;
        }
      });
      
      const results = await Promise.all(checkinsPromises);
      const validCheckins = results.filter(r => r !== null);
      
      // Sort by most recent first
      validCheckins.sort((a, b) => 
        new Date(b.checkin.created_at || b.checkin.submitted_at) - 
        new Date(a.checkin.created_at || a.checkin.submitted_at)
      );
      
      setRecentCheckins(validCheckins);
    } catch (error) {
      console.error('Failed to fetch check-ins:', error);
      toast.error('Failed to load weekly check-ins');
    } finally {
      setLoading(false);
    }
  };

  const openReviewModal = async (clientData, index) => {
    setSelectedIndex(index);
    setSelectedClient(clientData);
    
    // Fetch meal plan (axios interceptor handles auth)
    try {
      const response = await axios.get(`${API}/meal-plans/${clientData.client.id}/all`);
      
      const plans = response.data || [];
      const activePlan = plans.find(p => p.currently_selected) || plans[0];
      setMealPlan(activePlan);
    } catch (error) {
      console.error('Failed to load meal plan:', error);
      toast.error('Failed to load meal plan');
    }
  };

  const handleTierAdjustment = async (mealType, direction) => {
    if (!selectedClient || !mealPlan) return;
    
    setAdjusting(true);
    try {
      const response = await axios.post(
        `${API}/meal-plans/${selectedClient.client.id}/adjust-meal-tier`,
        {
          meal_identifier: mealType,
          direction: direction
        }
      );
      
      // Track the change
      const change = response.data;
      setTierChanges(prev => {
        // Remove any existing change for this meal
        const filtered = prev.filter(c => c.meal !== mealType);
        // Add new change
        return [...filtered, {
          meal: mealType,
          oldTier: change.old_tier,
          newTier: change.new_tier,
          calorieChange: change.calorie_change
        }];
      });
      
      toast.success(`${mealType} tier ${direction === 'up' ? 'increased' : 'decreased'}`);
      
      // Refresh meal plan
      const plansResponse = await axios.get(`${API}/meal-plans/${selectedClient.client.id}/all`);
      const plans = plansResponse.data || [];
      const activePlan = plans.find(p => p.currently_selected) || plans[0];
      setMealPlan(activePlan);
    } catch (error) {
      console.error('Failed to adjust tier:', error);
      toast.error(error.response?.data?.detail || 'Failed to adjust tier');
    } finally {
      setAdjusting(false);
    }
  };

  const handleNext = () => {
    if (selectedIndex < recentCheckins.length - 1) {
      const nextIndex = selectedIndex + 1;
      openReviewModal(recentCheckins[nextIndex], nextIndex);
    }
  };

  const handleMarkAsReviewed = async () => {
    console.log('[handleMarkAsReviewed] Button clicked');
    console.log('[handleMarkAsReviewed] selectedClient:', selectedClient);
    console.log('[handleMarkAsReviewed] checkIn ID:', selectedClient?.checkin?.id);
    
    if (!selectedClient?.checkin?.id) {
      console.error('[handleMarkAsReviewed] No check-in ID found!');
      toast.error('No check-in ID found');
      return;
    }
    
    setMarkingReviewed(true);
    try {
      console.log('[handleMarkAsReviewed] Sending request...');
      const response = await axios.post(
        `${API}/checkins/${selectedClient.checkin.id}/mark-reviewed`,
        { feedback: coachFeedback || null }
      );
      
      console.log('[handleMarkAsReviewed] Response:', response.data);
      toast.success('Check-in marked as reviewed!');
      
      // Force immediate notification refresh
      try {
        const notifRes = await axios.get(`${API}/notifications`);
        console.log('[handleMarkAsReviewed] Notifications refreshed:', notifRes.data);
      } catch (err) {
        console.log('[handleMarkAsReviewed] Could not refresh notifications:', err);
      }
      
      // Remove this client from the list immediately
      setRecentCheckins(prev => prev.filter(client => client.id !== selectedClient.id));
      
      // Close modal and reset state
      setSelectedClient(null);
      setTierChanges([]);
      setCoachFeedback('');
      
      // Refresh to ensure we have latest data
      await fetchRecentCheckins();
    } catch (error) {
      console.error('[handleMarkAsReviewed] Error:', error);
      const errorMsg = typeof error.response?.data?.detail === 'string' 
        ? error.response.data.detail 
        : 'Failed to mark as reviewed';
      toast.error(errorMsg);
    } finally {
      setMarkingReviewed(false);
    }
  };

  const handlePrevious = () => {
    if (selectedIndex > 0) {
      const prevIndex = selectedIndex - 1;
      openReviewModal(recentCheckins[prevIndex], prevIndex);
    }
  };

  // Group meals by meal_type and meal_number to get current tier
  const getMealTypeSummary = () => {
    if (!mealPlan || !mealPlan.recipes) return [];
    
    // Get recipes for current day (day 1 or recipes without day field)
    const currentDayRecipes = mealPlan.recipes.filter(r => !r.day || r.day === 1);
    
    console.log('[WeeklyReviews] Total recipes:', mealPlan.recipes.length);
    console.log('[WeeklyReviews] Current day recipes:', currentDayRecipes.length);
    
    // Build meal list with proper identification
    const meals = [];
    const seen = new Set();
    
    currentDayRecipes.forEach(recipe => {
      const type = recipe.meal_type;
      const mealNumber = recipe.meal_number || 1;
      
      // Create unique key for this meal
      const key = type === 'snack' ? `snack_${mealNumber}` : type;
      
      // Skip duplicates
      if (seen.has(key)) return;
      seen.add(key);
      
      // Extract tier from tier field or recipe_id
      let tier = recipe.tier || 1;
      
      const displayName = key.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
      
      meals.push({
        name: key,
        displayName: displayName,
        calories: recipe.total_kcal || 0,
        tier: tier,
        recipeId: recipe.recipe_id || recipe.library_recipe_id || '',
        mealNumber: mealNumber
      });
    });
    
    console.log('[WeeklyReviews] Meals found:', meals.map(m => m.name));
    
    // Order: breakfast, lunch, dinner, snack_1, snack_2, snack_3, snack_4, snack_5
    const order = ['breakfast', 'lunch', 'dinner', 'snack_1', 'snack_2', 'snack_3', 'snack_4', 'snack_5'];
    return order.map(key => meals.find(m => m.name === key)).filter(Boolean);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center h-96">
          <p className="text-gray-600">Loading weekly check-ins...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar user={user} onLogout={onLogout} />
      
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Weekly Check-in Reviews</h1>
          <p className="text-gray-600 mt-2">
            Review client progress and make tier adjustments
          </p>
        </div>

        {recentCheckins.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <MessageSquare className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No recent check-ins to review</p>
              <p className="text-sm text-gray-500 mt-2">Check-ins from the last 7 days will appear here</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {recentCheckins.map((data, index) => {
              const date = new Date(data.checkin.created_at || data.checkin.submitted_at);
              const formattedDate = date.toLocaleDateString('en-US', { 
                month: 'short', 
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              });
              
              return (
                <Card key={data.client.id} className="hover:shadow-lg transition-shadow">
                  <CardContent className="py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {data.client.first_name} {data.client.last_name}
                        </h3>
                        <p className="text-sm text-gray-600 mt-1">{formattedDate}</p>
                        <div className="flex gap-4 mt-2">
                          <Badge variant="outline" className="text-blue-600">
                            Score: {data.score}/5
                          </Badge>
                          {data.weeklyAvg && (
                            <Badge 
                              variant="outline" 
                              className={parseFloat(data.weeklyAvg) < 0 ? 'text-green-600' : 'text-orange-600'}
                            >
                              {data.weeklyAvg} kg/week
                            </Badge>
                          )}
                          {data.currentWeight && (
                            <Badge variant="outline" className="text-gray-600">
                              {data.currentWeight} kg
                            </Badge>
                          )}
                        </div>
                      </div>
                      <Button 
                        onClick={() => openReviewModal(data, index)}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        Quick Review
                        <ChevronRight className="ml-2 h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Review Modal */}
      <Dialog open={selectedClient !== null} onOpenChange={() => setSelectedClient(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-gradient-to-br from-white to-gray-50">
          <DialogHeader className="border-b pb-4">
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  {selectedClient?.client.first_name} {selectedClient?.client.last_name}
                </DialogTitle>
                <p className="text-sm text-gray-500 mt-1">Weekly Progress Review</p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedClient(null)}
                className="hover:bg-gray-100 rounded-full"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
          </DialogHeader>

          {selectedClient && (
            <div className="space-y-6 mt-6">
              {/* Overview Section */}
              <Card className="border-2 border-blue-100 shadow-md bg-gradient-to-br from-blue-50 to-white">
                <CardHeader className="pb-3">
                  <CardTitle className="text-xl font-bold text-gray-800 flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-blue-600" />
                    Progress Overview
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-6">
                    <div 
                      className="bg-white p-4 rounded-xl shadow-sm border border-blue-100 cursor-pointer hover:border-blue-300 hover:shadow-md transition-all"
                      onClick={() => setShowAnswersPopup(true)}
                      title="Click to view check-in answers"
                    >
                      <p className="text-sm font-medium text-gray-600 mb-1 flex items-center gap-1">
                        Adherence Score
                        <span className="text-xs text-blue-500">ⓘ</span>
                      </p>
                      <p className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-blue-500 bg-clip-text text-transparent">
                        {selectedClient.score}/5
                      </p>
                      <p className="text-xs text-gray-500 mt-1">Click to view answers</p>
                    </div>
                    <div className="bg-white p-4 rounded-xl shadow-sm border border-green-100">
                      <p className="text-sm font-medium text-gray-600 mb-1">Weight Progress</p>
                      <div className="space-y-1">
                        <p className="text-2xl font-bold text-gray-900">
                          {selectedClient.startingWeight ? `${selectedClient.startingWeight} kg` : 'N/A'} 
                          {' → '} 
                          {selectedClient.currentWeight ? `${selectedClient.currentWeight} kg` : 'N/A'}
                        </p>
                        {selectedClient.startingWeight && selectedClient.startingWeightDate && (
                          <p className="text-xs text-gray-500">
                            Start: {new Date(selectedClient.startingWeightDate).toLocaleDateString()}
                          </p>
                        )}
                        {selectedClient.currentWeight && selectedClient.currentWeightDate && (
                          <p className="text-xs text-gray-500">
                            Current: {new Date(selectedClient.currentWeightDate).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                      {selectedClient.weeklyAvg && (
                        <p className={`text-sm font-semibold mt-1 ${parseFloat(selectedClient.weeklyAvg) < 0 ? 'text-green-600' : 'text-orange-600'}`}>
                          {selectedClient.weeklyAvg} kg/week
                        </p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Quick Tier Adjustment */}
              {mealPlan && (
                <Card className="border-2 border-purple-100 shadow-md bg-gradient-to-br from-purple-50 to-white">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-xl font-bold text-gray-800">Quick Tier Adjustment</CardTitle>
                    <p className="text-sm text-gray-600 mt-1 flex items-center gap-1">
                      <span className="font-semibold text-purple-700">Target:</span> 
                      <span className="font-bold">{Math.round(mealPlan.target_calories || 0)} kcal/day</span>
                    </p>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {getMealTypeSummary().map((meal) => (
                        <div 
                          key={meal.name} 
                          className="flex items-center justify-between p-4 bg-white rounded-xl shadow-sm border-2 border-gray-100 hover:border-purple-200 transition-all"
                        >
                          <div className="flex-1">
                            <p className="font-bold text-lg text-gray-800">{meal.displayName}</p>
                            <div className="flex items-center gap-3 mt-1">
                              <span className="text-sm font-semibold text-purple-600">
                                {Math.round(meal.calories)} kcal
                              </span>
                              <span className="text-sm px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full font-semibold">
                                Tier {meal.tier}
                              </span>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleTierAdjustment(meal.name, 'down')}
                              disabled={adjusting}
                              className="hover:bg-red-50 hover:border-red-300 hover:text-red-600 transition-colors h-10 w-10 p-0"
                            >
                              <Minus className="h-5 w-5" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleTierAdjustment(meal.name, 'up')}
                              disabled={adjusting}
                              className="hover:bg-green-50 hover:border-green-300 hover:text-green-600 transition-colors h-10 w-10 p-0"
                            >
                              <TrendingUp className="h-5 w-5" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Coach Feedback & Review Section */}
              <Card className="bg-gradient-to-br from-purple-50 to-pink-50 border-purple-200">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-purple-900 text-lg mb-4">
                    📋 Coach Feedback (Optional)
                  </h3>
                  
                  {/* Quick Templates */}
                  <div className="mb-4">
                    <p className="text-sm text-purple-700 mb-2">Quick templates:</p>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setCoachFeedback("Keep doing what you're doing!")}
                        className="text-xs"
                      >
                        Keep going!
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setCoachFeedback("Great progress - no changes needed")}
                        className="text-xs"
                      >
                        Great progress
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setCoachFeedback("Increased calories to support your progress")}
                        className="text-xs"
                      >
                        Increased calories
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setCoachFeedback("Small adjustment to keep you progressing")}
                        className="text-xs"
                      >
                        Small adjustment
                      </Button>
                    </div>
                  </div>
                  
                  {/* Custom Message */}
                  <textarea
                    value={coachFeedback}
                    onChange={(e) => setCoachFeedback(e.target.value)}
                    placeholder="Or write a custom message..."
                    className="w-full px-3 py-2 border border-purple-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                    rows={3}
                  />
                  
                  {/* Summary of Changes */}
                  {tierChanges.length > 0 && (
                    <div className="mt-4 p-3 bg-white/70 rounded-lg border border-purple-100">
                      <p className="text-sm font-medium text-purple-800 mb-2">
                        Adjustments made:
                      </p>
                      {tierChanges.map((change, idx) => (
                        <p key={idx} className="text-xs text-purple-700">
                          • {change.meal}: {change.oldTier} → {change.newTier} kcal 
                          ({change.calorieChange > 0 ? '+' : ''}{change.calorieChange} kcal)
                        </p>
                      ))}
                    </div>
                  )}
                  
                  {/* Mark as Reviewed Button */}
                  <Button
                    onClick={handleMarkAsReviewed}
                    disabled={markingReviewed}
                    className="w-full mt-4 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-semibold"
                  >
                    {markingReviewed ? 'Saving...' : 'Mark as Reviewed & Notify Client'}
                  </Button>
                </CardContent>
              </Card>

              {/* Actions */}
              <div className="flex items-center justify-between pt-6 border-t-2 border-gray-200">
                <Button
                  variant="outline"
                  onClick={() => navigate(`/coach/client/${selectedClient.client.id}`)}
                  className="font-semibold hover:bg-blue-50 hover:border-blue-300 hover:text-blue-700"
                >
                  <ExternalLink className="mr-2 h-4 w-4" />
                  View Full Profile
                </Button>
                
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={handlePrevious}
                    disabled={selectedIndex === 0}
                    className="font-semibold disabled:opacity-50"
                  >
                    <ChevronLeft className="mr-2 h-4 w-4" />
                    Previous
                  </Button>
                  <Button
                    onClick={handleNext}
                    disabled={selectedIndex === recentCheckins.length - 1}
                    className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold disabled:opacity-50"
                  >
                    Next
                    <ChevronRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Check-in Answers Popup */}
      <Dialog open={showAnswersPopup} onOpenChange={setShowAnswersPopup}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              📋 Check-in Answers
            </DialogTitle>
          </DialogHeader>
          
          {selectedClient?.checkin && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                {/* Consistency */}
                <div className="bg-blue-50 p-3 rounded-lg border border-blue-100">
                  <p className="text-xs text-blue-600 font-medium mb-1">Consistency</p>
                  <p className="text-2xl font-bold text-blue-900">
                    {selectedClient.checkin.consistency || '—'}/5
                  </p>
                </div>

                {/* Weekend Adherence */}
                <div className="bg-purple-50 p-3 rounded-lg border border-purple-100">
                  <p className="text-xs text-purple-600 font-medium mb-1">Weekend</p>
                  <p className="text-2xl font-bold text-purple-900">
                    {selectedClient.checkin.weekend_adherence 
                      ? Math.ceil(selectedClient.checkin.weekend_adherence / 20)
                      : '—'}/5
                  </p>
                </div>

                {/* Hunger */}
                <div className="bg-orange-50 p-3 rounded-lg border border-orange-100">
                  <p className="text-xs text-orange-600 font-medium mb-1">🍽️ Hunger</p>
                  <p className="text-2xl font-bold text-orange-900">
                    {selectedClient.checkin.hunger || '—'}/5
                  </p>
                </div>

                {/* Energy */}
                <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-100">
                  <p className="text-xs text-yellow-600 font-medium mb-1">⚡ Energy</p>
                  <p className="text-2xl font-bold text-yellow-900">
                    {selectedClient.checkin.energy || '—'}/5
                  </p>
                </div>

                {/* Stress */}
                <div className="bg-indigo-50 p-3 rounded-lg border border-indigo-100">
                  <p className="text-xs text-indigo-600 font-medium mb-1">🧘 Stress</p>
                  <p className="text-2xl font-bold text-indigo-900">
                    {selectedClient.checkin.stress_level || '—'}/5
                  </p>
                </div>

                {/* Sleep */}
                <div className="bg-violet-50 p-3 rounded-lg border border-violet-100">
                  <p className="text-xs text-violet-600 font-medium mb-1">😴 Sleep</p>
                  <p className="text-2xl font-bold text-violet-900">
                    {selectedClient.checkin.sleep_quality || '—'}/5
                  </p>
                </div>
              </div>

              {/* Notes if any */}
              {selectedClient.checkin.notes && (
                <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                  <p className="text-xs text-gray-600 font-medium mb-1">📝 Client Notes</p>
                  <p className="text-sm text-gray-800">{selectedClient.checkin.notes}</p>
                </div>
              )}

              {/* Current Weight */}
              <div className="bg-green-50 p-3 rounded-lg border border-green-100">
                <p className="text-xs text-green-600 font-medium mb-1">⚖️ Weight</p>
                <p className="text-2xl font-bold text-green-900">
                  {selectedClient.checkin.weight || selectedClient.currentWeight || '—'} kg
                </p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
